import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { GuestInfo } from '@proofroom/schema';
import { share, type ShareState } from '../api/endpoints';
import { ApiError } from '../api/client';
import { useSession } from '../auth/session';
import { EmptyState, ErrorState, Spinner } from '../components/ui';
import { GuestDraftViewer } from './DraftViewer';

const CLOSED: Record<Exclude<ShareState['state'], 'ok'>, { title: string; body: string }> = {
  unknown: { title: 'This link does not open anything', body: 'Check the address you were sent; a character may be missing.' },
  expired: { title: 'This link has expired', body: 'Ask whoever sent it for a new one.' },
  revoked: { title: 'This link has been withdrawn', body: 'Ask whoever sent it if there is a newer one.' },
};

/** What the unlock route's refusals mean to the person typing: the codes come from the server's HttpError envelope. */
function unlockMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'bad_password') return 'That password is not right.';
    if (err.code === 'rate_limited') return err.message;
    if (err.status === 404 || err.status === 410) return 'This link is no longer open.';
    return err.message;
  }
  return 'Could not open the proof';
}

/**
 * /s/:token, outside RequireAuth: whoever has the link. Unknown, expired and revoked links get a
 * plain message; an open one asks for a name and the password, and then, or straight away when
 * the cookie already belongs to this link, shows the guest viewer.
 */
export function Share() {
  const { token = '' } = useParams();
  const { user, guest, setGuest, leave, logout } = useSession();
  const qc = useQueryClient();
  const state = useQuery({ queryKey: ['share', token], queryFn: () => share.state(token), retry: false });
  // the guest this screen itself unlocked: shown the viewer at once, without waiting on the state route to see the new cookie
  const [unlocked, setUnlocked] = useState<GuestInfo | null>(null);

  // the state route names the caller's own guest when their cookie is this link's; adopt that (or its absence) as the session
  useEffect(() => {
    if (state.data) setGuest(state.data.guest);
  }, [state.data, setGuest]);

  const onLeave = async () => {
    await leave();
    setUnlocked(null);
    await qc.invalidateQueries({ queryKey: ['share', token] });
  };

  if (state.isPending) return <div className="login"><Spinner label="Checking the link" /></div>;
  if (state.isError) return <div className="login"><ErrorState title="Could not check the link" error={state.error} retry={() => void state.refetch()} /></div>;
  const s = state.data;
  if (s.state !== 'ok') {
    const closed = CLOSED[s.state];
    return <div className="login"><div className="card"><EmptyState title={closed.title}>{closed.body}</EmptyState></div></div>;
  }
  // this link's guest, and not one carried over from another link's cookie
  const mine = guest && (s.guest?.id === guest.id || unlocked?.id === guest.id);
  if (mine) return <GuestDraftViewer guest={guest} onLeave={() => void onLeave()} />;
  // A signed-in user's session always wins over a guest cookie on the server, so unlocking here
  // would open the viewer as them: a dead end for a client outside the project, and a guest badge
  // over a member's own comments for anyone else. Say so, rather than offering the form.
  if (user) {
    return (
      <div className="login">
        <div className="card">
          <h1>{s.documentName}</h1>
          <p className="sub">{s.projectName}{s.label ? ` · ${s.label}` : ''}</p>
          <p className="small muted" style={{ marginBottom: 16 }}>You are signed in as {user.name}. This link is for someone without a Proofroom account; open the document from your projects, or sign out to open it as a guest.</p>
          <button className="btn" type="button" onClick={() => void logout()} style={{ width: '100%', height: 36, justifyContent: 'center' }}>Sign out</button>
        </div>
      </div>
    );
  }
  return <UnlockForm token={token} state={s} onUnlocked={(g) => { setUnlocked(g); setGuest(g); }} />;
}

function UnlockForm({ token, state, onUnlocked }: { token: string; state: ShareState; onUnlocked(guest: GuestInfo): void }) {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const { guest } = await share.unlock(token, name.trim(), password);
      onUnlocked(guest);
    } catch (err) {
      setError(unlockMessage(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="login">
      <div className="card">
        <h1>{state.documentName}</h1>
        <p className="sub">{state.projectName}{state.label ? ` · ${state.label}` : ''}</p>
        <p className="small muted" style={{ marginBottom: 16 }}>You have been sent a proof to look at and comment on. Enter your name and the password that came with the link.</p>
        <form onSubmit={submit}>
          <div className="field">
            <label className="label" htmlFor="guest-name">Your name</label>
            <input id="guest-name" className="input" autoComplete="name" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </div>
          <div className="field">
            <label className="label" htmlFor="guest-password">Password</label>
            <input id="guest-password" className="input" type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {error && <p className="error-text" role="alert" style={{ marginBottom: 12 }}>{error}</p>}
          <button className="btn primary" type="submit" disabled={busy || !name.trim() || !password} style={{ width: '100%', height: 36, justifyContent: 'center' }}>{busy ? 'Opening' : 'Open the proof'}</button>
        </form>
      </div>
    </div>
  );
}
